import { useCallback, useState, type ReactNode, type Ref } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { Iframe } from '@/components/ui/Iframe';
import { Box, XStack, YStack } from '@/components/ui/layout/layout';

import {
  BROWSER_STREAM_IFRAME_SANDBOX,
  getInteractiveBrowserStreamUrl,
} from './browser-stream-url';
import { setBrowserStreamInputEnabled } from './use-browser-stream-input';

type Props = {
  children?: ReactNode;
  streamUrl?: string;
  isPaused?: boolean;
  canResume?: boolean;
  onResume?: () => void;
  canInteract: boolean;
  iframeRef?: Ref<HTMLIFrameElement>;
  isLoading: boolean;
  isFullscreen: boolean;
  canToggleFullscreen?: boolean;
  canClose?: boolean;
  onToggleFullscreen: () => void;
  onClose: () => void;
  onRefresh: () => void;
  onTakeControl: () => void;
  onReturnControl: () => void;
};

export const LiveBrowserStreamCard = ({
  children,
  streamUrl,
  isPaused = false,
  canResume = true,
  onResume,
  canInteract,
  iframeRef,
  isLoading,
  isFullscreen,
  canToggleFullscreen = true,
  canClose = true,
  onToggleFullscreen,
  onClose,
  onRefresh,
  onTakeControl,
  onReturnControl,
}: Props) => {
  const [frameGeneration, setFrameGeneration] = useState(0);
  const frameUrl = getInteractiveBrowserStreamUrl(streamUrl);
  const showFrame = !isPaused && Boolean(frameUrl);
  const interactive = showFrame && canInteract;
  const setFrameRef = useCallback(
    (frame: HTMLIFrameElement | null) => {
      if (frame) setBrowserStreamInputEnabled(frame, interactive);
      if (typeof iframeRef === 'function') return iframeRef(frame);
      if (iframeRef) iframeRef.current = frame;
    },
    [iframeRef, interactive]
  );
  const handleRefresh = useCallback(() => {
    setFrameGeneration((generation) => generation + 1);
    onRefresh();
  }, [onRefresh]);

  return (
    <YStack
      className="h-full min-h-0"
      data-mode={interactive ? 'interactive' : 'view_only'}
    >
      <XStack className="items-center justify-between gap-2 border-b border-border px-3 py-2">
        <XStack className="min-w-0 items-center gap-2">
          <Icons.www className="size-4 shrink-0 text-foreground-muted" />
          <span className="truncate text-sm font-medium">Live browser</span>
          {interactive ? (
            <span className="text-xs text-foreground-muted">You control</span>
          ) : null}
        </XStack>
        <XStack className="shrink-0 items-center gap-1">
          {!isPaused ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={!showFrame}
              onClick={interactive ? onReturnControl : onTakeControl}
            >
              {interactive ? 'Return control' : 'Take control'}
            </Button>
          ) : null}
          {!interactive && !isPaused ? (
            <IconButton
              size="sm"
              icon={Icons.refresh}
              aria-label="Refresh live browser"
              onClick={handleRefresh}
            />
          ) : null}
          {canToggleFullscreen ? (
            <IconButton
              size="sm"
              icon={isFullscreen ? Icons.minimize : Icons.maximize}
              aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
              onClick={onToggleFullscreen}
            />
          ) : null}
          {canClose ? (
            <IconButton
              size="sm"
              icon={Icons.cancel}
              aria-label="Hide live browser"
              onClick={onClose}
            />
          ) : null}
        </XStack>
      </XStack>
      <Box className="relative min-h-0 flex-1 bg-gray-12" tabIndex={-1}>
        {showFrame ? (
          <Iframe
            key={frameGeneration}
            ref={setFrameRef}
            navigation="same-origin"
            title={
              interactive
                ? 'Interactive browser session'
                : 'Live browser session'
            }
            src={frameUrl}
            className="absolute inset-0 h-full w-full border-0"
            sandbox={BROWSER_STREAM_IFRAME_SANDBOX}
            referrerPolicy="no-referrer"
          />
        ) : (
          <YStack
            center
            className="absolute inset-0 gap-3 px-6 text-center text-sm text-foreground-muted"
          >
            {isPaused ? (
              <Button
                size="sm"
                variant="secondary"
                disabled={!canResume}
                onClick={onResume}
              >
                Resume
              </Button>
            ) : isLoading ? (
              'Connecting to browser…'
            ) : (
              'Live browser unavailable.'
            )}
          </YStack>
        )}
        {showFrame && !interactive ? (
          <Box
            className="absolute inset-0"
            aria-hidden="true"
            onPointerDown={(event) => {
              event.preventDefault();
              event.currentTarget.parentElement?.focus({ preventScroll: true });
            }}
          />
        ) : null}
        {children}
      </Box>
    </YStack>
  );
};
